import { Download, File } from 'lucide-react';
import { Upload } from 'antd';
import React from 'react';
import { usePapaParse } from 'react-papaparse';
import { toast } from 'react-toastify';
import readXlsxFile from 'read-excel-file';
import styled from 'styled-components';
import { utils, writeFileXLSX } from 'xlsx';
import { AlertCall } from '../containers_v2/alert/AlertProvider';
import { launchToastError } from '../containers_v2/forms/visual-form-editor/visualFormEditor';
import { dummyRequest, getBase64 } from '../containers_v2/import/UploadFile';
import { DefaultButton } from '../styles/global/css/GlobalButton';
import { PrimaryColor } from '../styles/global/css/Utils';
import { Translate, translateToString } from '../styles/global/translate';
const { readString } = usePapaParse();


type HeaderFallback<HeaderType> = (header: unknown) => HeaderType | undefined;
function parseHeaders<HeaderType>(headers: unknown[], fallback: HeaderFallback<HeaderType>): Map<number, HeaderType> {
	return headers.reduce<Map<number, HeaderType>>((acc, header, index) => {
		const newHeader = fallback(header);
		if (newHeader) {
			acc.set(index, newHeader);
		}
		return acc;
	}, new Map());
}

export type CustomErrorThrower = (error: string) => void;
export const customErrorThrower = (headerIndex: number): CustomErrorThrower => (error: string) => {
	throw `${error} Column ${headerIndex + 1}`;
};

export type WrongTypeErrorThrower = (columnName: string, value: unknown, expectedType: string) => void;
export const wrongTypeErrorThrower = (headerIndex: number): WrongTypeErrorThrower => (columnName: string, value: unknown, expectedType: string) => {
	customErrorThrower(headerIndex)(`Expected '${expectedType}' for ${columnName} got '${typeof value}'.`);
};

export type MandatoryFieldEnforcer = (value: unknown | undefined, fieldName: string) => unknown;
export function mandatoryFieldEnforcer<T>(value: T | undefined, fieldName: string): T {
	if (value == undefined) throw 'Missing mandatory field ' + fieldName;
	return value;
}

type SwitchHeader<HeaderType, T> = (row: Partial<T>, value: unknown, header: HeaderType, headerIndex: number, customErrorThrower: CustomErrorThrower, wrongTypeErrorThrower: WrongTypeErrorThrower) => Partial<T>;
function parseRows<HeaderType, T extends object>(
	headers: Map<number, HeaderType>,
	rows: unknown[],
	switchHeader: SwitchHeader<HeaderType, T>
): Partial<T>[] {
	return rows.reduce<Partial<T>[]>((acc, row, rowIndex) => {
		try {
			if (rowIndex === 0) return acc;
			if (!Array.isArray(row)) {
				throw `Expected array got ${typeof row}`;
			}
			if (row.length == 0 || (row.length == 1 && !row[0])) return acc;
			let res: Partial<T> = {};
			for (const [headerIndex, header] of headers.entries()) {
				let value = row[headerIndex];
				if (value === undefined || value === '' || value === null) continue;
				try {
					value = JSON.parse(value as string);
				} catch (_) { /* empty */ }
				res = switchHeader(res, value, header, headerIndex, customErrorThrower(headerIndex), wrongTypeErrorThrower(headerIndex));
			}
			acc.push(res);
		} catch (e) {
			throw `Import error: ${e} line ${rowIndex}`;
		}
		return acc;
	}, []);
}

async function readFile<HeaderType, T extends object>(
	data: unknown[],
	switchHeader: SwitchHeader<HeaderType, T>,
	unPartialType: (data: Partial<T>, missingFieldErrorThrower: MandatoryFieldEnforcer) => T | undefined,
	headerFallback: HeaderFallback<HeaderType>,
	headersFilter?: HeadersFilter<HeaderType>
): Promise<T[]> {
	try {
		if (!Array.isArray(data[0])) throw 'Expected array headers got ' + typeof data[0];
		let headers = parseHeaders(data[0], headerFallback);
		if (headersFilter !== undefined) {
			headers = await headersFilter(headers);
		}
		if (headers.size === 0) return [];
		return parseRows<HeaderType, T>(headers, data, switchHeader).reduce<T[]>((acc, row) => {
			const result = unPartialType(row, mandatoryFieldEnforcer);
			if (result) acc.push(result);
			return acc;
		}, []);
	} catch (e) {
		launchToastError(e);
		return [];
	}
}

const Button = styled(DefaultButton)`
    width: 200px;
    height: 32px;
`;

const ButtonText = styled.p`
	padding: 0 1em;
	font-size: 13px;
`;

export function importWrapper<Template extends object>(onFileLoaded: (data: unknown[]) => void, alert: AlertCall, genTemplate?: () => Template[]) {
	function importFile(file: File) {
		getBase64(file, async loadedFile => {
			if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
				readXlsxFile(loadedFile).then(onFileLoaded);
			} else {
				readString(loadedFile, {
					complete: (res) => {
						if (res.errors.length !== 0) {
							toast.error((translateToString('import.csv_error.' + res.errors[0].type) as string).replace('{{ ROW }}', (res.errors[0].row + 1).toString()).replace('{{ SEPARATOR }}', res.meta.delimiter), {
								position: 'top-right',
								autoClose: 4000,
								hideProgressBar: true,
								closeOnClick: true,
								pauseOnHover: true,
								draggable: true,
								progress: undefined
							});
							return;
						}
						onFileLoaded(res.data);
					},
					worker: true
				});
			}
		});
	}
	const accept = '.csv,.xlsx';
	if (genTemplate) {
		const { Dragger } = Upload;
		alert({
			noButtons: true,
			content: (closeModal, __) => <>
				{/* @ts-expect-error library nonsence */}
				<Dragger
					style={{ width: '100%' }}
					showUploadList={false}
					name='import'
					onChange={(info) => {
						if (info.file.status === 'done' && info.file.originFileObj) {
							closeModal();
							importFile(info.file.originFileObj);
						}
					}}
					customRequest={dummyRequest}
					accept={accept}
				>
					<div><File size={35}/></div>
					<Button><Translate id="import.from_file" /></Button>
					<ButtonText><Translate id="import.click_or_drag" /></ButtonText>
					<p className="ant-upload-hint" style={{ fontSize: 12 }}><Translate id="import.must_be_a_csv_or_xlsx" /></p>
				</Dragger>
				<a href="" onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					closeModal();
					const rows = genTemplate!();
					const ws = utils.json_to_sheet(rows);
					const wb = utils.book_new();
					utils.book_append_sheet(wb, ws, 'Data');
					writeFileXLSX(wb, 'template.xlsx');

				}} style={{ fontSize: 13, color: PrimaryColor, display: 'flex', gap: '3px' }}><Translate id="download_template" /><Download color={PrimaryColor} /></a>
			</>
		});
		return;
	}
	const input = document.createElement('input');
	input.type = 'file';
	input.accept = accept;
	input.onchange = async(e) => {
		const file = (e.target as HTMLInputElement).files?.[0];
		if (!file) {
			launchToastError('No file selected');
			return;
		}
		importFile(file);
	};
	input.click();
}


type HeadersFilter<HeaderType> = (headers: Map<number, HeaderType>) => Promise<Map<number, HeaderType>>
export default function importAndReadTableFile<HeaderType extends string | number, T extends object, Template extends object>(props: {
	alert: AlertCall,
	switchHeader: SwitchHeader<HeaderType, T>,
	unPartialType: (row: Partial<T>, missingFieldErrorThrower: MandatoryFieldEnforcer) => T | undefined,
	onDataRead: (data: T[]) => void,
	headerFallback: HeaderFallback<HeaderType>,
	headersFilter?: HeadersFilter<HeaderType>,
	onFileLoaded?: () => void,
	genTemplate?: () => Template[]
}) {
	importWrapper(async(data) => {
		props.onFileLoaded?.();
		const rows = await readFile(data, props.switchHeader, props.unPartialType, props.headerFallback, props.headersFilter);
		props.onDataRead(rows);
	}, props.alert, props.genTemplate);
}
